import { isAxiosError } from 'axios';

import { formatCompactNumber } from '../../../ui/utils/format-number';

import type { GetBillingOverviewResponseDtoTopUp } from '../../../api/generated/model/getBillingOverviewResponseDtoTopUp';

export const formatCredits = (n: number): string => Math.max(0, Math.floor(n)).toLocaleString();

export const formatCreditsShort = (n: number): string =>
  formatCompactNumber(Math.max(0, Math.floor(n)));

export const formatUsd = (cents: number | null): string => {
  if (cents === null) return '—';
  return cents % 100 === 0 ? `$${(cents / 100).toLocaleString()}` : `$${(cents / 100).toFixed(2)}`;
};

const CARD_BRANDS: Record<string, string> = {
  amex: 'American Express',
  diners: 'Diners Club',
  discover: 'Discover',
  jcb: 'JCB',
  mastercard: 'Mastercard',
  unionpay: 'UnionPay',
  visa: 'Visa',
};

export const cardBrandLabel = (brand: string): string =>
  CARD_BRANDS[brand.toLowerCase()] ?? brand.charAt(0).toUpperCase() + brand.slice(1);

/** The saved payment method as the overview carries it: any Stripe type, details only where that type has them. */
export type PaymentMethodLike = { type: string; brand: string | null; last4: string | null; email: string | null };

const PAYMENT_METHOD_TYPES: Record<string, string> = {
  amazon_pay: 'Amazon Pay',
  bacs_debit: 'Bacs Direct Debit',
  cashapp: 'Cash App Pay',
  link: 'Link',
  paypal: 'PayPal',
  revolut_pay: 'Revolut Pay',
  sepa_debit: 'SEPA Direct Debit',
  us_bank_account: 'Bank account',
};

/** "Visa" for a card, "Chase" for a bank debit, "Link" / "Cash App Pay" for a method with no brand. */
export const paymentMethodName = (method: PaymentMethodLike): string =>
  method.brand
    ? cardBrandLabel(method.brand)
    : (PAYMENT_METHOD_TYPES[method.type] ?? cardBrandLabel(method.type.replace(/_/g, ' ')));

/** One line for prose: "Visa •••• 9308", "Chase •••• 6789", "Link · owner@x.test", "Cash App Pay". */
export const paymentMethodLabel = (method: PaymentMethodLike): string => {
  const name = paymentMethodName(method);
  if (method.last4) return `${name} •••• ${method.last4}`;
  return method.email ? `${name} · ${method.email}` : name;
};

export const apiErrorMessage = (error: unknown, fallback: string): string => {
  if (isAxiosError<{ message?: unknown }>(error)) {
    const message = error.response?.data?.message;
    if (typeof message === 'string' && message.trim()) return message;
    if (Array.isArray(message) && typeof message[0] === 'string') return message[0];
  }
  return fallback;
};

export const TOP_UP_BLOCKED_COPY: Record<NonNullable<NonNullable<GetBillingOverviewResponseDtoTopUp>['blockedReason']>, string> = {
  no_subscription: 'Start your subscription first.',
  trial: 'Top-ups start with the paid plan. Upgrade to add credits now.',
  inactive: 'Your plan is not active. Update your card or re-subscribe in Manage plan first.',
  dispute: 'Top-ups are not available while a payment dispute is open.',
};
